import { useCallback, useEffect, useState } from 'react';
import { fetchOrgUsers, deleteOrgUser, suspendOrgUser, activateOrgUser } from '../api/client';
import Modal from './Modal';
import UserFormModal from './UserFormModal';
import './UsersPanel.css';

const DELETE_CONFIRM_PHRASE = 'delete user';

export default function UsersPanel() {
  const [users, setUsers] = useState(null);
  const [limit, setLimit] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [formUser, setFormUser] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [confirmSuspendUser, setConfirmSuspendUser] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await fetchOrgUsers();
      setUsers(data.users);
      setLimit(data.limit);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const atLimit = limit != null && users != null && users.length >= limit;

  function handleSaved(user, { created } = {}) {
    setFormUser(null);
    load();
    setNotice(created ? `Invitation sent to ${user.email}.` : 'User updated.');
  }

  async function handleToggleSuspend(user) {
    setBusyId(user.id);
    setError('');
    try {
      const action = user.status === 4 ? activateOrgUser : suspendOrgUser;
      await action(user.id);
      await load();
      return true;
    } catch (err) {
      setError(err.message || 'Failed to update user');
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function handleConfirmSuspend() {
    const ok = await handleToggleSuspend(confirmSuspendUser);
    if (ok) setConfirmSuspendUser(null);
  }

  function closeDeleteModal() {
    setConfirmDeleteUser(null);
    setDeleteConfirmText('');
  }

  async function handleDelete(user) {
    setBusyId(user.id);
    setError('');
    try {
      await deleteOrgUser(user.id);
      await load();
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete user');
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function handleConfirmDelete() {
    const ok = await handleDelete(confirmDeleteUser);
    if (ok) closeDeleteModal();
  }

  const deleteConfirmMatches = deleteConfirmText.trim().toLowerCase() === DELETE_CONFIRM_PHRASE;

  return (
    <div className="users-panel">
      <div className="users-panel-header">
        <div>
          <h3>Organization users</h3>
          <p>Create and manage the people who can sign in under your organization.</p>
          {users && (
            <p className="users-count">
              {users.length}/{limit != null ? limit : '∞'} users
            </p>
          )}
        </div>
        <button
          className="users-btn-primary"
          onClick={() => setFormUser({})}
          disabled={atLimit}
          title={atLimit ? `You've reached the ${limit}-user limit for the Enterprise plan` : undefined}
        >
          Add user
        </button>
      </div>

      {atLimit && (
        <p className="users-notice users-notice--warn">
          You've reached the {limit}-user limit for the Enterprise plan. Upgrade to Enterprise Advanced for
          unlimited users.
        </p>
      )}

      {notice && <p className="users-notice">{notice}</p>}
      {error && <p className="users-error">{error}</p>}

      {!users && !error && <p className="users-placeholder">Loading users…</p>}
      {users && users.length === 0 && <p className="users-placeholder">No users yet. Add one to get started.</p>}

      {users && users.length > 0 && (
        <div className="users-list">
          {users.map((u) => {
            const suspended = u.status === 4;
            const busy = busyId === u.id;
            return (
              <div key={u.id} className="users-row">
                <div className="users-row-main">
                  <div className="users-row-name">{u.fullname}</div>
                  <div className="users-row-meta">
                    {u.username} · {u.email}
                  </div>
                </div>
                <span className={`users-badge ${u.role === 1 ? 'users-badge--admin' : 'users-badge--role'}`}>
                  {u.roleLabel}
                </span>
                <span className={`users-badge ${suspended ? 'users-badge--suspended' : 'users-badge--active'}`}>
                  {suspended ? 'Suspended' : 'Active'}
                </span>
                <div className="users-row-actions">
                  <button className="users-action-btn" onClick={() => setFormUser(u)} disabled={busy}>
                    Edit
                  </button>
                  <button
                    className="users-action-btn"
                    onClick={() => (suspended ? handleToggleSuspend(u) : setConfirmSuspendUser(u))}
                    disabled={busy}
                  >
                    {suspended ? 'Activate' : 'Suspend'}
                  </button>
                  <button
                    className="users-action-btn users-action-btn--danger"
                    onClick={() => setConfirmDeleteUser(u)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formUser && (
        <UserFormModal user={formUser.id ? formUser : null} onClose={() => setFormUser(null)} onSaved={handleSaved} />
      )}

      {confirmSuspendUser && (
        <Modal title="Suspend user" onClose={() => setConfirmSuspendUser(null)}>
          <p className="users-confirm-modal-text">
            Suspend <strong>{confirmSuspendUser.fullname}</strong>? They'll still be able to log in, but won't be
            able to send chat messages until you reactivate them.
          </p>
          {error && <p className="users-error">{error}</p>}
          <div className="users-confirm-modal-actions">
            <button
              className="users-action-btn"
              onClick={() => setConfirmSuspendUser(null)}
              disabled={busyId === confirmSuspendUser.id}
            >
              Cancel
            </button>
            <button
              className="users-action-btn users-action-btn--danger"
              onClick={handleConfirmSuspend}
              disabled={busyId === confirmSuspendUser.id}
            >
              {busyId === confirmSuspendUser.id ? 'Suspending…' : 'Suspend user'}
            </button>
          </div>
        </Modal>
      )}

      {confirmDeleteUser && (
        <Modal title="Delete user" onClose={closeDeleteModal}>
          <p className="users-confirm-modal-text">
            You're about to permanently delete <strong>{confirmDeleteUser.fullname}</strong>.{' '}
            <strong>This action cannot be reversed</strong> — their account, access, and history with your
            organization will be gone for good.
          </p>
          <label className="users-confirm-input-field">
            <span>
              Type <strong>{DELETE_CONFIRM_PHRASE}</strong> to confirm
            </span>
            <input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={busyId === confirmDeleteUser.id}
              autoFocus
              autoComplete="off"
            />
          </label>
          {error && <p className="users-error">{error}</p>}
          <div className="users-confirm-modal-actions">
            <button className="users-action-btn" onClick={closeDeleteModal} disabled={busyId === confirmDeleteUser.id}>
              Cancel
            </button>
            <button
              className="users-action-btn users-action-btn--danger"
              onClick={handleConfirmDelete}
              disabled={busyId === confirmDeleteUser.id || !deleteConfirmMatches}
            >
              {busyId === confirmDeleteUser.id ? 'Deleting…' : 'Delete user'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
